import React from 'react';
import { useCart } from '../context/CartContext';

// Componente para exibir item no carrinho
const CartItem = ({ item, onUpdateQuantity, onRemoveItem }) => {
  return (
    <div className="flex items-center justify-between py-4 border-b border-gray-200">
      <div className="flex items-center">
        {item.imagem ? (
          <img 
            src={item.imagem} 
            alt={item.nome} 
            className="w-16 h-16 object-contain rounded-lg mr-4"
            onError={(e) => {
              e.target.src = 'https://placehold.co/80x80?text=Imagem'; // Placeholder se imagem falhar
            }}
          />
        ) : (
          <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 mr-4 flex items-center justify-center">
            <span className="text-gray-500 text-xs">Sem imagem</span>
          </div>
        )}
        <div>
          <h3 className="font-semibold text-gray-800">{item.nome}</h3>
          <p className="text-violet-600 font-bold">R$ {item.preco.toFixed(2)}</p>
        </div>
      </div>
      
      <div className="flex items-center">
        <div className="flex items-center border border-gray-300 rounded-lg mr-4">
          <button
            onClick={() => onUpdateQuantity(item._id, item.quantidade - 1)}
            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-l-lg"
          >
            -
          </button>
          <span className="px-3 py-1">{item.quantidade}</span>
          <button
            onClick={() => onUpdateQuantity(item._id, item.quantidade + 1)}
            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-r-lg"
          >
            +
          </button>
        </div>
        
        <div className="text-right mr-4">
          <p className="font-semibold text-gray-800">R$ {(item.preco * item.quantidade).toFixed(2)}</p>
        </div>
        
        <button
          onClick={() => onRemoveItem(item._id)}
          className="text-red-500 hover:text-red-700"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Componente principal da página do carrinho
const CartPage = ({ onVoltar, onCheckout }) => {
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  
  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout();
    } else {
      // Navegar para página de checkout
      alert('Funcionalidade de checkout será implementada em breve!');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F6FC]">
      <div className="container mx-auto p-4 sm:p-6 md:p-8">
        {/* Cabeçalho */}
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Seu Carrinho
            </h1>
            <p className="text-gray-600 mt-1">Revise seus itens selecionados</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onVoltar}
              className="bg-violet-100 text-violet-700 font-semibold px-4 py-2 rounded-lg hover:bg-violet-200 transition-all duration-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Continuar comprando
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lista de itens */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6">
              {cart.items.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v5a2 2 0 01-2 2H9a2 2 0 01-2-2v-5m6-5V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2" />
                  </svg>
                  <h3 className="text-xl font-medium text-gray-900 mb-1">Seu carrinho está vazio</h3>
                  <p className="text-gray-500 mb-4">Comece adicionando alguns produtos</p>
                  <button
                    onClick={onVoltar}
                    className="bg-violet-600 text-white px-6 py-2 rounded-lg hover:bg-violet-700 transition-colors"
                  >
                    Ir para a loja
                  </button>
                </div>
              ) : (
                <div>
                  {cart.items.map((item) => (
                    <CartItem 
                      key={item._id} 
                      item={item} 
                      onUpdateQuantity={updateQuantity}
                      onRemoveItem={removeFromCart}
                    />
                  ))}
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold text-lg">R$ {cart.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Frete</span>
                      <span className="font-semibold text-lg">R$ 0,00</span>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                      <span className="text-gray-800 font-bold">Total</span>
                      <span className="font-bold text-xl text-violet-600">R$ {cart.total.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-8 space-x-4">
                    <button
                      onClick={clearCart}
                      className="px-6 py-3 border border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Limpar Carrinho
                    </button>
                    <button
                      onClick={handleCheckout}
                      className="px-6 py-3 bg-violet-600 text-white font-semibold rounded-lg hover:bg-violet-700 transition-colors flex-1 max-w-xs"
                    >
                      Finalizar Compra
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Resumo do pedido */}
          <div>
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Resumo do Pedido</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Produtos ({cart.itemCount})</span>
                  <span className="font-medium">R$ {cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Frete</span>
                  <span className="font-medium">R$ 0,00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Descontos</span>
                  <span className="font-medium text-green-600">R$ 0,00</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-lg font-bold text-gray-800">Total</span>
                <span className="text-xl font-bold text-violet-600">R$ {cart.total.toFixed(2)}</span>
              </div>
              
              <button
                onClick={handleCheckout}
                className="w-full mt-6 bg-violet-600 text-white font-semibold py-3 rounded-lg hover:bg-violet-700 transition-colors"
              >
                Finalizar Compra
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;